import React from 'react';
import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './PlatformAdmin.module.scss';

/**
 * Reusable pager for the admin list endpoints (server-side pagination).
 * Server responses include `total`; page count = ceil(total / limit).
 *
 * Props:
 *   - total, limit, offset
 *   - onOffsetChange(newOffset)
 *   - loading?: boolean (disables the buttons while a page is fetching)
 *   - unit?: string (label, e.g. "users")
 */
const PaginationBar = ({ total = 0, limit = 50, offset = 0, onOffsetChange, loading = false, unit = 'results' }) => {
  const safeLimit = limit || 50;
  const page = Math.floor(offset / safeLimit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / safeLimit));
  const canPrev = offset > 0;
  const canNext = offset + safeLimit < total;

  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + safeLimit, total);

  return (
    <Box className={styles.pagination}>
      <span>
        {total === 0 ? `0 ${unit}` : `${from}–${to} of ${total} ${unit}`} · Page {page} of {totalPages}
      </span>
      <IconButton
        size="small"
        className={styles.subtleBtn}
        disabled={!canPrev || loading}
        onClick={() => onOffsetChange(Math.max(0, offset - safeLimit))}
        title="Previous page"
      >
        <ChevronLeft size={18} />
      </IconButton>
      <IconButton
        size="small"
        className={styles.subtleBtn}
        disabled={!canNext || loading}
        onClick={() => onOffsetChange(offset + safeLimit)}
        title="Next page"
      >
        <ChevronRight size={18} />
      </IconButton>
    </Box>
  );
};

export default PaginationBar;
